import {
    USER_SEGMENTATION_TEAM_TYPE_DESIGN,
    USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT,
    USER_SEGMENTATION_TEAM_TYPE_MARKETING,
    USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT,
    USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT,
    USER_SEGMENTATION_TEAM_TYPE_SOFTWARE
} from "@atlassian/onboarding-survey";

import { JiraFFLContentGenerationResponse } from "@/libraries/bxp-web-stargate-client/src";

import { type TableRowProps } from "../sections/cfind-4666-frictionless-first-look/paid-jira-ai-preview-modal/preview-table/table-row";

export const ANALYTIC_EVENT_ACTION_SUCCEEDED = "succeeded";
export const ANALYTIC_EVENT_ACTION_STARTED = "started";
export const ANALYTIC_EVENT_ACTION_FAILED = "failed";
export const MADLIB_CONTENT_GENERATION_ANALYTIC_EVENT_NAME =
    "madlib content generation";
export const MADLIB_FLOW = "madlib";

export const teamTypesMap = {
    [USER_SEGMENTATION_TEAM_TYPE_SOFTWARE]: {
        messageKey: "softwareDevelopment"
    },
    [USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT]: {
        messageKey: "productManagement"
    },
    [USER_SEGMENTATION_TEAM_TYPE_MARKETING]: {
        messageKey: "marketing"
    },
    [USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT]: {
        messageKey: "projectManagement"
    },
    [USER_SEGMENTATION_TEAM_TYPE_DESIGN]: {
        messageKey: "design"
    },
    [USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT]: {
        messageKey: "it"
    }
} as const;

export type TeamType = keyof typeof teamTypesMap;

export const getMessageKeyForTeamType = (teamType: TeamType) =>
    teamTypesMap[teamType].messageKey;

export const teamNamesMap: Record<TeamType, string> = {
    [USER_SEGMENTATION_TEAM_TYPE_DESIGN]: "Design Team",
    [USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT]: "IT Support Team",
    [USER_SEGMENTATION_TEAM_TYPE_MARKETING]: "Marketing Team",
    [USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT]: "Product Management Team",
    [USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT]: "Project Management Team",
    [USER_SEGMENTATION_TEAM_TYPE_SOFTWARE]: "Software Team"
};

export const formatCgsData = (
    cgsData: JiraFFLContentGenerationResponse | undefined
): Omit<TableRowProps, "isLastRow">[] | undefined => {
    if (!cgsData) {
        return undefined;
    }

    const epicDueInDays = [14, 21, 28, 33, 39, 45];
    const taskDueInDays = [2, 3, 7, 8, 10, 14, 17, 21, 24];
    const taskPriorities: TableRowProps["priority"][] = [
        "medium",
        "low",
        "low",
        "low",
        "medium",
        "medium",
        "low",
        "medium",
        "medium",
        "low"
    ];
    const assignees = [
        "Jenna Tran",
        "Raul Gonzales",
        "Aoife Burke",
        "Jenna Tran",
        "Omar Salah",
        "Bradley Philips",
        "Raul Gonzales",
        "Aoife Burke",
        "Bradley Philips",
        "Athena Ai"
    ];
    const assigneeAvatars = {
        "Aoife Burke":
            "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12EHXB.webp",
        "Athena Ai":
            "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12EHXE.webp",
        "Bradley Philips":
            "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12EHXC.webp",
        "Jenna Tran":
            "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12EHXF.webp",
        "Omar Salah":
            "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12EHXG.webp",
        "Raul Gonzales":
            "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12EHXD.webp"
    };

    const epicData = cgsData.sampleIssues.epic;
    const workItemsData = cgsData.sampleIssues.childWorkItems;
    const finalData: Omit<TableRowProps, "isLastRow">[] = [];
    if (epicData) {
        const dueDate = new Date();
        dueDate.setDate(dueDate.getDate() + (epicDueInDays.shift() || 0));
        const assignee = assignees.shift() || (assignees[0] as string);

        finalData.push({
            type: "epic",
            summary: epicData.epicName,
            status: "in progress",
            dueDate,
            priority: "high",
            assignee,
            assigneeImgSrc:
                assigneeAvatars[assignee as keyof typeof assigneeAvatars]
        });
    }

    if (workItemsData && workItemsData.length > 0) {
        workItemsData.forEach((item) => {
            const dueDate = new Date();
            dueDate.setDate(dueDate.getDate() + (taskDueInDays.shift() || 0));
            const assignee = assignees.shift() || (assignees[0] as string);

            finalData.push({
                type: "task",
                summary: item.summary,
                status: "to do",
                dueDate,
                priority: taskPriorities.shift() || "medium",
                assignee,
                assigneeImgSrc:
                    assigneeAvatars[assignee as keyof typeof assigneeAvatars]
            });
        });
    }

    return finalData;
};

// fallback data for when api request to csg fails
export const fallbackCgsData: JiraFFLContentGenerationResponse = {
    cacheKey: "default",
    sampleIssues: {
        childWorkItems: [
            {
                summary:
                    "Audit existing infrastructure dependencies for migration readiness"
            },
            {
                summary:
                    "Provision the target environment with high availability configurations"
            },
            {
                summary:
                    "Implement automated health checks for post-migration validation"
            },
            {
                summary:
                    "Execute data migration to the new platform environment"
            },
            {
                summary:
                    "Perform reliability testing on the migrated platform in staging"
            }
        ],
        epic: {
            description:
                "What are we doing? We are executing a migration of the existing platform to a new infrastructure or environment. Why are we doing it? To achieve the goal of improving the overall reliability of our software services. What does success look like? All platform components are successfully transitioned to the new environment and operate with improved reliability. Assumption: Specific reliability targets and target environment details are TBD.",
            epicName: "Platform migration"
        },
        spaceName: "Software Development Project"
    }
};
